import type { QueryClient, QueryKey } from '@tanstack/react-query'

type TaskRecord = Record<string, unknown>

const stripUndefinedValues = (value: TaskRecord): TaskRecord => {
  const next: TaskRecord = {}
  Object.entries(value).forEach(([key, itemValue]) => {
    if (itemValue !== undefined) {
      next[key] = itemValue
    }
  })
  return next
}

const preserveAvailableStatuses = (
  cachedTask: TaskRecord,
  updates: TaskRecord,
): TaskRecord => {
  if (
    (!Object.prototype.hasOwnProperty.call(updates, 'availableStatuses') ||
      updates.availableStatuses == null) &&
    Object.prototype.hasOwnProperty.call(cachedTask, 'availableStatuses')
  ) {
    return {
      ...updates,
      availableStatuses: cachedTask.availableStatuses,
    }
  }

  return updates
}

const mergeTaskPatch = (
  cachedTask: TaskRecord,
  patch: TaskRecord,
): TaskRecord => {
  const sanitizedPatch = stripUndefinedValues(patch)
  const mergedPatch = preserveAvailableStatuses(cachedTask, sanitizedPatch)
  const nextPatch = Object.prototype.hasOwnProperty.call(mergedPatch, 'groupId')
    ? {
        ...mergedPatch,
        group:
          mergedPatch.group &&
          (mergedPatch.group as TaskRecord).id === mergedPatch.groupId
            ? mergedPatch.group
            : cachedTask.group &&
                (cachedTask.group as TaskRecord).id === mergedPatch.groupId
              ? cachedTask.group
              : null,
      }
    : mergedPatch

  return {
    ...cachedTask,
    ...nextPatch,
  }
}

type UpdateOperation = (qc: QueryClient) => void
type StoreOperation = () => void

export class RealtimeCacheUpdater {
  private queryClient: QueryClient
  private queue: UpdateOperation[] = []
  private storeQueue: StoreOperation[] = []
  private scheduled = false
  private batchMs: number

  constructor(queryClient: QueryClient, batchMs: number = 120) {
    this.queryClient = queryClient
    this.batchMs = batchMs
  }

  schedule(op: UpdateOperation) {
    this.queue.push(op)
    if (!this.scheduled) {
      this.scheduled = true
      window.setTimeout(() => this.flush(), this.batchMs)
    }
  }

  private flush() {
    const ops = this.queue
    const storeOps = this.storeQueue
    this.queue = []
    this.storeQueue = []
    this.scheduled = false
    if (ops.length === 0) return
    // Execute ops in a single frame to encourage React 18 batching
    const run = () => {
      for (const op of ops) op(this.queryClient)
      for (const sop of storeOps) sop()
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run)
    } else {
      run()
    }
  }

  // Common helpers
  mergeIntoArrayCache<T extends { id: string }>(key: QueryKey, item: T) {
    this.schedule(qc => {
      qc.setQueryData<T[] | undefined>(key, old => {
        const list = old || []
        const idx = list.findIndex(t => t.id === item.id)
        if (idx !== -1) {
          const copy = list.slice()
          copy[idx] = mergeTaskPatch(
            list[idx] as TaskRecord,
            item as TaskRecord,
          ) as T
          return copy
        }
        // Replace an optimistic temp entry if present
        const tempIdx = list.findIndex(
          t => typeof t.id === 'string' && t.id.startsWith('temp-'),
        )
        if (tempIdx !== -1) {
          const copy = list.slice()
          copy[tempIdx] = item
          return copy
        }
        return [...list, item]
      })
    })
  }

  /**
   * Update task fields in cache. Uses targeted approach - only calls setQueryData
   * on queries that ACTUALLY contain the task, avoiding any iteration over unrelated queries.
   */
  updateTaskFields<T extends { id: string }>(
    taskId: string,
    partial: Partial<T>,
  ) {
    this.schedule(qc => {
      // Update single task detail cache if it exists
      const detailKey = ['task', taskId]
      const detailData = qc.getQueryData<any>(detailKey)
      if (detailData && detailData.id === taskId) {
        qc.setQueryData(detailKey, {
          ...(mergeTaskPatch(detailData as TaskRecord, {
            ...(partial as TaskRecord),
            _realtime_updated: Date.now(),
          }) as TaskRecord),
        })
      }

      // Update task detail caches keyed by queryKeys.tasks.detail/byNumber
      const detailQueries = qc.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'tasks' &&
          (q.queryKey[1] === 'detail' || q.queryKey[1] === 'by-number'),
      })

      for (const [queryKey, oldData] of detailQueries) {
        if (!oldData || oldData.id !== taskId) continue
        qc.setQueryData(
          queryKey,
          mergeTaskPatch(oldData as TaskRecord, {
            ...(partial as TaskRecord),
            _realtime_updated: Date.now(),
          }),
        )
      }

      // Find and update ONLY queries that actually contain this task
      // Include both 'tasks' and 'my-tasks' queries
      const allQueries = qc.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] === 'tasks' || q.queryKey[0] === 'my-tasks'),
      })

      for (const [queryKey, oldData] of allQueries) {
        if (!oldData) continue

        const taskArray = Array.isArray(oldData)
          ? oldData
          : oldData.tasks || oldData.data
        if (!Array.isArray(taskArray)) continue

        const idx = taskArray.findIndex((t: any) => t.id === taskId)
        if (idx === -1) continue // Task not in this query - skip entirely

        // Only update this specific query
        const newArray = [...taskArray]
        newArray[idx] = mergeTaskPatch(taskArray[idx] as TaskRecord, {
          ...(partial as TaskRecord),
          _realtime_updated: Date.now(),
        }) as T

        if (Array.isArray(oldData)) {
          qc.setQueryData(queryKey, newArray)
        } else if (oldData.tasks) {
          qc.setQueryData(queryKey, {
            ...oldData,
            tasks: newArray,
            _lastUpdated: Date.now(),
          })
        } else if (oldData.data) {
          qc.setQueryData(queryKey, {
            ...oldData,
            data: newArray,
            _lastUpdated: Date.now(),
          })
        }
      }
    })
  }

  /**
   * Remove a task from main task lists. Uses targeted approach - only calls setQueryData
   * on queries that ACTUALLY contain the task.
   */
  removeTaskFromMainLists(taskId: string) {
    this.schedule(qc => {
      // Find and update ONLY queries that contain this task
      // Include both 'tasks' and 'my-tasks' queries
      const allQueries = qc.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] === 'tasks' || q.queryKey[0] === 'my-tasks'),
      })

      for (const [queryKey, oldData] of allQueries) {
        if (!oldData) continue

        const taskArray = Array.isArray(oldData)
          ? oldData
          : oldData.tasks || oldData.data
        if (!Array.isArray(taskArray)) continue

        const idx = taskArray.findIndex((t: any) => t.id === taskId)
        if (idx === -1) continue // Task not in this query - skip entirely

        // Only update this specific query
        const filtered = taskArray.filter((t: any) => t.id !== taskId)

        if (Array.isArray(oldData)) {
          qc.setQueryData(queryKey, filtered)
        } else if (oldData.tasks) {
          qc.setQueryData(queryKey, { ...oldData, tasks: filtered })
        } else if (oldData.data) {
          qc.setQueryData(queryKey, { ...oldData, data: filtered })
        }
      }
    })
  }

  invalidate(
    key: QueryKey,
    refetchType: 'active' | 'inactive' | 'all' | 'none' = 'none',
  ) {
    this.schedule(qc => {
      qc.invalidateQueries({ queryKey: key as any, refetchType })
    })
  }

  // Zustand/store updates can also be coalesced
  scheduleStore(op: StoreOperation) {
    this.storeQueue.push(op)
    if (!this.scheduled) {
      this.scheduled = true
      window.setTimeout(() => this.flush(), this.batchMs)
    }
  }
}

export function createRealtimeCacheUpdater(
  queryClient: QueryClient,
  batchMs?: number,
) {
  return new RealtimeCacheUpdater(queryClient, batchMs)
}
